import React, { useMemo, useState } from "react";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { useReportQuery } from "../shared/useReportQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { formatMetric, METRIC_LABELS } from "../shared/format";
import { TOKEN_TENDER_CAVEAT } from "../shared/metricDefinitions";
import { MetricKey, ReportDimension } from "../api/types";

// The legacy Ticketing Financial report's 11-column layout (Date is the dimension/label column). The
// canonical Gross/Net come straight from the engine's FinancialByDimension definitions; the deductions,
// the refund columns, Merchant Processed, and the tender-tip richness are the additive decompositions.
const LEGACY_COLUMNS: MetricKey[] = [
  "Gross",
  "NetSales",
  "DiscountExPromo",
  "PromoDiscount",
  "RefundsPreTax",
  "Tax",
  "Surcharge",
  "Refunds",
  "MerchantProcessed",
  "TransactionCount",
];

// Richness beyond the legacy 11 — the decomposition detail that makes the report auditable.
const RICHNESS_COLUMNS: MetricKey[] = [
  "Tip",
  "TenderCreditTip",
  "TenderRfidTip",
  "Discount",
  "TenderUntenderedAmount",
  "OrderCount",
];

const DISPLAY_COLUMNS: MetricKey[] = [...LEGACY_COLUMNS, ...RICHNESS_COLUMNS];

// Exact legacy column headers (the rest fall back to METRIC_LABELS).
const COLUMN_HEADERS: Partial<Record<MetricKey, string>> = {
  Gross: "Total Gross Sales",
  NetSales: "Total Net Sales",
  DiscountExPromo: "Net Deduction - Discounts",
  PromoDiscount: "Net Deduction - Promo Codes",
  RefundsPreTax: "Net Deduction - Refunds",
  Tax: "Net Deduction - Taxes",
  Surcharge: "Total Service Fee",
  Refunds: "Total Refunds Value",
  MerchantProcessed: "Total Merchant Processed",
  TransactionCount: "Total # of Transactions",
  Tip: "Tips (total)",
  Discount: "Discounts (combined)",
  OrderCount: "Orders",
};

const SUMMARY_METRICS: MetricKey[] = [
  "NetSales",
  "Gross",
  "MerchantProcessed",
  "Refunds",
  "Discount",
  "TransactionCount",
];

const DIMENSIONS: { value: ReportDimension; label: string; columnHeader: string }[] = [
  { value: "Day", label: "Day", columnHeader: "Date" },
  { value: "Event", label: "Event", columnHeader: "Event" },
  { value: "Vendor", label: "Vendor", columnHeader: "Vendor" },
  { value: "Location", label: "Location", columnHeader: "Location" },
  { value: "VendorType", label: "Vendor Type", columnHeader: "Vendor Type" },
  { value: "Clerk", label: "Clerk", columnHeader: "Clerk" },
];

const TOOLTIP =
  "Ticketing Financial report from the NEW unified reporting engine — the legacy 11-column ticketing " +
  "layout, restricted to ticketing-channel orders. Net Sales and Gross are the CANONICAL definitions " +
  "(Gross = order subtotal + tax; Net = real-money net) shared with the Financial reports — the legacy " +
  "old-net formula is NOT used. Discounts vs Promo Codes, the pre-tax and gross refund figures, the " +
  "per-tender tips, and Merchant Processed are additive decompositions that reconcile to those totals. " +
  "Default group-by is Day (the legacy per-date rows); switch the dimension to re-pivot. Money in USD.";

export default function TicketingFinancialReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [dimension, setDimension] = useState<ReportDimension>("Day");

  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, dimension });
  const state = useReportQuery(
    submitted
      ? toQueryParams("TicketingFinancial", submitted.filters, { dimension: submitted.dimension })
      : null
  );

  const activeDimension = submitted?.dimension ?? dimension;
  const cfg = DIMENSIONS.find((d) => d.value === activeDimension) ?? DIMENSIONS[0];

  // Present the curated legacy-order columns (only those the engine actually advertised), with the exact
  // legacy headers. The engine still returns the full per-tender breakdown in each row for the CSV/audit.
  const gridData = useMemo(() => {
    if (!state.data) return null;
    const advertised = new Set(state.data.metrics);
    return { ...state.data, metrics: DISPLAY_COLUMNS.filter((m) => advertised.has(m)) };
  }, [state.data]);

  return (
    <ReportPageShell
      title="Ticketing Financial"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      actions={
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          Group by
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value as ReportDimension)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {DIMENSIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {SUMMARY_METRICS.filter((m) => (gridData ?? data).metrics.includes(m) || m in data.totals).map(
              (m) => (
                <StatCard
                  key={m}
                  label={COLUMN_HEADERS[m] ?? METRIC_LABELS[m]}
                  value={formatMetric(m, data.totals[m])}
                  emphasis={m === "NetSales"}
                />
              )
            )}
          </div>
          <MetricGrid
            data={gridData ?? data}
            keyColumnHeader={cfg.columnHeader}
            metricLabelOverrides={COLUMN_HEADERS}
            exportName={`TicketingFinancial_${activeDimension}`}
            csvExtra={[
              `NOTE: Discounts + Promo Codes = total Discounts; per-tender tips sum to total Tips; ` +
                `Refunds (pre-tax) is the product portion of the gross Total Refunds Value; ` +
                `tender sales + Untendered = Gross.`,
              `NOTE: Merchant Processed is the actual card/RFID amount the processor settled ` +
                `(charged − refunded), not a reconstruction from the sales columns.`,
              `NOTE: ${TOKEN_TENDER_CAVEAT}`,
            ]}
          />
          <p className="mt-2 text-xs text-gray-500">
            <span className="font-semibold">Net Sales</span> and <span className="font-semibold">Gross</span>{" "}
            are the canonical engine definitions (shared with the Financial reports). The deduction columns,
            refund figures, per-tender tips, and Merchant Processed are additive decompositions that reconcile
            to those totals.
          </p>
        </>
      )}
    </ReportPageShell>
  );
}
